import {
  CorrectGuessesSchema,
  SetCorrectGuessesBodySchema,
} from '@pin/schemas/admin';
import { firstValueFrom } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { timeout } from 'rxjs/operators';
import * as z from 'zod';
import { withIdToken } from '../firebase/auth';
import { adminApiUrl } from '../utils/context';
import { HttpError, retryWithBackoff } from '../utils/rxjs.http';

export const getCorrectGuesses = (participantId: string): Promise<string[]> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${adminApiUrl}/api/results/participant/${participantId}/correct-guesses`,
        {
          headers: { Authorization: `Bearer ${token}` },
          selector: async (res) => {
            if (!res.ok) throw new HttpError(res);
            const data = CorrectGuessesSchema.parse(await res.json());
            return data.normalizedNames;
          },
        }
      ).pipe(timeout(10_000), retryWithBackoff(2))
    )
  );

export const setCorrectGuesses = (
  participantId: string,
  normalizedNames: z.input<
    typeof SetCorrectGuessesBodySchema
  >['normalizedNames']
): Promise<string[]> => {
  const body = SetCorrectGuessesBodySchema.parse({ normalizedNames });
  return firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${adminApiUrl}/api/results/participant/${participantId}/correct-guesses`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
          selector: async (res) => {
            if (!res.ok) throw new HttpError(res);
            const data = CorrectGuessesSchema.parse(await res.json());
            return data.normalizedNames;
          },
        }
      ).pipe(timeout(10_000))
    )
  );
};

export const clearCorrectGuesses = (participantId: string): Promise<void> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${adminApiUrl}/api/results/participant/${participantId}/correct-guesses`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          selector: async (res) => {
            if (!res.ok) {
              throw new HttpError(res);
            }
          },
        }
      ).pipe(timeout(10_000))
    )
  );

export const createGuessDistribution = (): Promise<void> => {
  const searchParams = new URLSearchParams({});
  if (import.meta.env.DEV) {
    searchParams.append('includeTestUsers', 'true');
  }
  return firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${adminApiUrl}/api/results/guess-distribution?${searchParams}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          selector: async (res) => {
            if (!res.ok) {
              throw new HttpError(res);
            }
          },
        }
      ).pipe(timeout(10_000))
    )
  );
};
